import { useState, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload,
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Banknote,
  X,
  Calendar,
  Info,
  Mail,
  Users,
  BarChart3,
  Package,
  CreditCard,
} from 'lucide-react';

interface Transaction {
  hesap: string;
  tahsilatYilAy: string;
  tahsilatTarihi: string;
  mahsupTarihi: string;
  valorTarihi: string;
  mahsupNo: string;
  aciklama: string;
  odemeBicimi: string;
  borc: number;
  alacak: number;
  kalan: number;
}

interface DateGroup {
  date: string;
  parsedDate: Date;
  transactions: Transaction[];
  totalBorc: number;
  totalAlacak: number;
  endingBalance: number;
}

interface SettlementWeek {
  wednesdayDate: string;
  wednesdayParsed: Date;
  periodGroups: DateGroup[];
  periodBorc: number;
  periodAlacak: number;
  payment: number;
  totalTxCount: number;
}

interface SettlementMonth {
  key: string;
  label: string;
  weeks: SettlementWeek[];
  totalPayment: number;
  totalBorc: number;
  totalAlacak: number;
}

const MONTH_NAMES = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const MUSTERIX_SCREENSHOTS = [
  { name: 'customers', label: 'Anlık Raporlama' },
  { name: 'statistics', label: 'Ürün Detayı' },
  { name: 'collections', label: 'Müşteri Yönetimi' },
  { name: 'product-detail', label: 'Derin Raporlama' },
  { name: 'tahsilat-takibi', label: 'Tahsilat Takibi' },
];

const MUSTERIX_FEATURES = [
  { icon: Users, label: 'Müşteri ve ziyaret yönetimi' },
  { icon: CreditCard, label: 'Haftalık tahsilat planlaması' },
  { icon: Package, label: 'Ürün maliyeti ve fiyat takibi' },
  { icon: BarChart3, label: 'Canlı satış ve kâr analitiği' },
];

function parseNumber(val: string | undefined): number {
  if (!val || val === '-' || val === '') return 0;
  const cleaned = val.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

function parseDateDMY(dateStr: string): Date | null {
  if (!dateStr || dateStr === 'Toplam') return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day);
}

function formatCurrency(amount: number): string {
  const abs = Math.abs(amount);
  return abs.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateLong(dateStr: string): string {
  const d = parseDateDMY(dateStr);
  if (!d) return dateStr;
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}, ${DAY_NAMES[d.getDay()]}`;
}

function formatDateShort(d: Date): string {
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
}

function getNextWednesday(): Date {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const dayOfWeek = now.getDay();
  if (dayOfWeek === 3) return now;
  const daysUntilWed = (3 - dayOfWeek + 7) % 7;
  const wed = new Date(now);
  wed.setDate(wed.getDate() + (daysUntilWed === 0 ? 7 : daysUntilWed));
  return wed;
}

function parseExcelData(rows: (string | undefined)[][]): DateGroup[] {
  const transactions: Transaction[] = [];
  const headerRow = rows.findIndex(r =>
    r[0]?.toString().trim() === 'Hesap' ||
    (r[8]?.toString().trim() === 'Borç' && r[9]?.toString().trim() === 'Alacak')
  );

  const dataStart = headerRow >= 0 ? headerRow + 1 : 2;

  for (let i = dataStart; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[0]) continue;
    if (row[3] === 'Toplam') continue;

    const tx: Transaction = {
      hesap: (row[0] || '').toString().trim(),
      tahsilatYilAy: (row[1] || '').toString().trim(),
      tahsilatTarihi: (row[2] || '').toString().trim(),
      mahsupTarihi: (row[3] || '').toString().trim(),
      valorTarihi: (row[4] || '').toString().trim(),
      mahsupNo: (row[5] || '').toString().trim(),
      aciklama: (row[6] || '').toString().trim(),
      odemeBicimi: (row[7] || '').toString().trim(),
      borc: parseNumber(row[8]?.toString()),
      alacak: parseNumber(row[9]?.toString()),
      kalan: parseNumber(row[10]?.toString()),
    };

    if (tx.tahsilatTarihi && (tx.borc !== 0 || tx.alacak !== 0 || tx.kalan !== 0)) {
      transactions.push(tx);
    }
  }

  const groupMap = new Map<string, Transaction[]>();
  transactions.forEach(tx => {
    const key = tx.tahsilatTarihi;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(tx);
  });

  const groups: DateGroup[] = [];
  groupMap.forEach((txs, dateStr) => {
    const parsed = parseDateDMY(dateStr);
    if (!parsed) return;
    const totalBorc = txs.reduce((s, t) => s + t.borc, 0);
    const totalAlacak = txs.reduce((s, t) => s + t.alacak, 0);
    const lastTx = txs[txs.length - 1];
    groups.push({
      date: dateStr,
      parsedDate: parsed,
      transactions: txs,
      totalBorc,
      totalAlacak,
      endingBalance: lastTx.kalan,
    });
  });

  groups.sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  return groups;
}

function buildSettlementWeek(
  thisWed: DateGroup,
  prevWed: DateGroup | null,
  groups: DateGroup[],
  payment: number,
): SettlementWeek {
  const periodGroups = groups.filter(g => {
    if (prevWed) {
      return g.parsedDate.getTime() > prevWed.parsedDate.getTime() &&
             g.parsedDate.getTime() <= thisWed.parsedDate.getTime();
    }
    return g.parsedDate.getTime() <= thisWed.parsedDate.getTime();
  });

  return {
    wednesdayDate: thisWed.date,
    wednesdayParsed: thisWed.parsedDate,
    periodGroups,
    periodBorc: periodGroups.reduce((s, g) => s + g.totalBorc, 0),
    periodAlacak: periodGroups.reduce((s, g) => s + g.totalAlacak, 0),
    payment,
    totalTxCount: periodGroups.reduce((s, g) => s + g.transactions.length, 0),
  };
}

function computeSettlements(groups: DateGroup[]): {
  upcoming: SettlementWeek[];
  past: SettlementWeek[];
} {
  const wednesdays = groups.filter(g => g.parsedDate.getDay() === 3);
  if (wednesdays.length === 0) return { upcoming: [], past: [] };

  const nextWed = getNextWednesday();
  const firstUpcomingIdx = wednesdays.findIndex(w => w.parsedDate.getTime() >= nextWed.getTime());

  // --- Upcoming settlements ---
  // First upcoming Wednesday: payment = full ending balance (user's manual
  // payments aren't in the Excel, so the current balance IS what they owe).
  // Subsequent Wednesdays: delta from previous Wednesday with carry-forward
  // (works because we know the user will pay each Wednesday going forward).
  const upcoming: SettlementWeek[] = [];
  if (firstUpcomingIdx >= 0) {
    let carry = 0;
    for (let i = firstUpcomingIdx; i < wednesdays.length; i++) {
      const thisWed = wednesdays[i];
      const prevWed = i > 0 ? wednesdays[i - 1] : null;

      let payment: number;
      if (i === firstUpcomingIdx) {
        payment = Math.max(0, thisWed.endingBalance);
        carry = Math.min(0, thisWed.endingBalance);
      } else {
        const delta = thisWed.endingBalance - wednesdays[i - 1].endingBalance;
        const net = carry + delta;
        payment = Math.max(0, net);
        carry = Math.min(0, net);
      }

      upcoming.push(buildSettlementWeek(thisWed, prevWed, groups, payment));
    }
  }

  // --- Past settlements (no payment calc, just raw period data) ---
  const pastEnd = firstUpcomingIdx >= 0 ? firstUpcomingIdx : wednesdays.length;
  const past: SettlementWeek[] = [];
  for (let i = 0; i < pastEnd; i++) {
    const thisWed = wednesdays[i];
    const prevWed = i > 0 ? wednesdays[i - 1] : null;
    past.push(buildSettlementWeek(thisWed, prevWed, groups, thisWed.endingBalance));
  }

  return { upcoming, past };
}

function groupSettlementsByMonth(settlements: SettlementWeek[]): SettlementMonth[] {
  const monthMap = new Map<string, SettlementWeek[]>();

  settlements.forEach(s => {
    const key = `${s.wednesdayParsed.getFullYear()}-${String(s.wednesdayParsed.getMonth() + 1).padStart(2, '0')}`;
    if (!monthMap.has(key)) monthMap.set(key, []);
    monthMap.get(key)!.push(s);
  });

  const result: SettlementMonth[] = [];
  monthMap.forEach((weeks, key) => {
    const [year, month] = key.split('-').map(Number);
    result.push({
      key,
      label: `${MONTH_NAMES[month - 1]} ${year}`,
      weeks,
      totalPayment: weeks.reduce((s, w) => s + w.payment, 0),
      totalBorc: weeks.reduce((s, w) => s + w.periodBorc, 0),
      totalAlacak: weeks.reduce((s, w) => s + w.periodAlacak, 0),
    });
  });

  result.sort((a, b) => a.key.localeCompare(b.key));
  return result;
}

const NeOdeyecegim = () => {
  const [groups, setGroups] = useState<DateGroup[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'upcoming' | 'monthly'>('upcoming');
  const [carouselIdx, setCarouselIdx] = useState(0);

  const handleFile = useCallback((file: File) => {
    setError(null);
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Lütfen bir Excel dosyası (.xlsx veya .xls) yükleyin.');
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<(string | undefined)[]>(sheet, {
          header: 1,
          raw: false,
        });
        const parsed = parseExcelData(rows);
        if (parsed.length === 0) {
          setError('Excel dosyasında işlem bulunamadı. Lütfen doğru dosyayı yüklediğinizden emin olun.');
          return;
        }
        setGroups(parsed);
        setExpandedWeeks(new Set());
        setExpandedDays(new Set());
        setExpandedMonths(new Set());
      } catch {
        setError('Excel dosyası okunamadı. Lütfen dosyanın bozuk olmadığından emin olun.');
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const toggleWeek = useCallback((key: string) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleDay = useCallback((key: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleMonth = useCallback((key: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const computed = useMemo(() => {
    if (!groups || groups.length === 0) return null;

    const { upcoming, past } = computeSettlements(groups);
    if (upcoming.length === 0 && past.length === 0) return null;

    const thisWeek = upcoming.length > 0 ? upcoming[0] : null;

    const allSettlements = [...past, ...upcoming];
    const allMonths = groupSettlementsByMonth(allSettlements);

    const totalTxCount = groups.reduce((s, g) => s + g.transactions.length, 0);

    return {
      upcoming,
      past,
      thisWeek,
      allMonths,
      totalTxCount,
    };
  }, [groups]);

  const clearData = useCallback(() => {
    setGroups(null);
    setFileName(null);
    setError(null);
    setExpandedWeeks(new Set());
    setExpandedDays(new Set());
    setExpandedMonths(new Set());
  }, []);

  const renderTransactionTable = (group: DateGroup) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-100/50">
            <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Açıklama</th>
            <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">Mahsup No</th>
            <th className="text-right px-4 py-2 text-xs font-semibold text-red-500 uppercase">Borç</th>
            <th className="text-right px-4 py-2 text-xs font-semibold text-green-500 uppercase">Alacak</th>
            <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Bakiye</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {group.transactions.map((tx, idx) => (
            <tr key={idx} className="hover:bg-white transition-colors">
              <td className="px-4 py-2 text-slate-700 max-w-[180px] sm:max-w-[280px] truncate">
                {tx.aciklama || '-'}
              </td>
              <td className="px-4 py-2 text-slate-500 font-mono text-xs hidden sm:table-cell">
                {tx.mahsupNo}
              </td>
              <td className="px-4 py-2 text-right font-medium tabular-nums">
                {tx.borc > 0 ? (
                  <span className="text-red-600">₺{formatCurrency(tx.borc)}</span>
                ) : (
                  <span className="text-slate-300">-</span>
                )}
              </td>
              <td className="px-4 py-2 text-right font-medium tabular-nums">
                {tx.alacak > 0 ? (
                  <span className="text-green-600">₺{formatCurrency(tx.alacak)}</span>
                ) : (
                  <span className="text-slate-300">-</span>
                )}
              </td>
              <td className={`px-4 py-2 text-right font-bold tabular-nums ${tx.kalan > 0 ? 'text-red-600' : tx.kalan < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                {tx.kalan < 0 ? '-' : ''}₺{formatCurrency(tx.kalan)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-100/80 font-semibold">
            <td className="px-4 py-2 text-slate-700">Toplam</td>
            <td className="hidden sm:table-cell"></td>
            <td className="px-4 py-2 text-right text-red-600 tabular-nums">₺{formatCurrency(group.totalBorc)}</td>
            <td className="px-4 py-2 text-right text-green-600 tabular-nums">₺{formatCurrency(group.totalAlacak)}</td>
            <td className={`px-4 py-2 text-right tabular-nums ${group.endingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {group.endingBalance < 0 ? '-' : ''}₺{formatCurrency(group.endingBalance)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  const renderSettlementWeek = (week: SettlementWeek, showLabel?: string) => {
    const weekKey = week.wednesdayDate;
    const isExpanded = expandedWeeks.has(weekKey);
    const hasPay = week.payment > 0;

    return (
      <div key={weekKey}>
        <button
          onClick={() => toggleWeek(weekKey)}
          className="w-full flex items-center justify-between px-4 sm:px-6 py-4 hover:bg-slate-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`
              w-10 h-10 rounded-xl flex items-center justify-center shrink-0
              ${hasPay ? 'bg-red-50' : 'bg-green-50'}
            `}>
              {hasPay ? (
                <ArrowUpRight className="w-5 h-5 text-red-500" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-700 truncate">
                  {formatDateLong(week.wednesdayDate)}
                </p>
                {showLabel && (
                  <Badge variant="outline" className="text-xs shrink-0">{showLabel}</Badge>
                )}
              </div>
              <p className="text-xs text-slate-400">
                {week.periodGroups.length} gün &middot; {week.totalTxCount} işlem
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              {hasPay ? (
                <p className="text-sm font-bold text-red-600 tabular-nums">
                  ₺{formatCurrency(week.payment)}
                </p>
              ) : (
                <p className="text-sm font-bold text-green-600">
                  Ödeme yok
                </p>
              )}
              <p className="text-xs text-slate-400 tabular-nums">
                B: ₺{formatCurrency(week.periodBorc)} · A: ₺{formatCurrency(week.periodAlacak)}
              </p>
            </div>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="border-t border-slate-100 bg-slate-50/30">
            {week.periodGroups.map((dayGroup) => {
              const dayKey = `${weekKey}_${dayGroup.date}`;
              const isDayExpanded = expandedDays.has(dayKey);
              const isWednesday = dayGroup.parsedDate.getDay() === 3;

              return (
                <div key={dayGroup.date} className={isWednesday ? 'bg-orange-50/30' : ''}>
                  <button
                    onClick={() => toggleDay(dayKey)}
                    className="w-full flex items-center justify-between px-6 sm:px-10 py-3 hover:bg-white/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${isWednesday ? 'bg-orange-400' : 'bg-slate-300'}`} />
                      <p className={`text-sm truncate ${isWednesday ? 'font-semibold text-orange-700' : 'text-slate-600'}`}>
                        {formatDateLong(dayGroup.date)}
                      </p>
                      <span className="text-xs text-slate-400">{dayGroup.transactions.length} işlem</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right text-xs tabular-nums">
                        {dayGroup.totalBorc > 0 && (
                          <span className="text-red-500 mr-2">+₺{formatCurrency(dayGroup.totalBorc)}</span>
                        )}
                        {dayGroup.totalAlacak > 0 && (
                          <span className="text-green-500">-₺{formatCurrency(dayGroup.totalAlacak)}</span>
                        )}
                      </div>
                      {isDayExpanded ? (
                        <ChevronUp className="w-3 h-3 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-3 h-3 text-slate-400" />
                      )}
                    </div>
                  </button>
                  {isDayExpanded && (
                    <div className="px-4 sm:px-8 pb-3">
                      {renderTransactionTable(dayGroup)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMusterixSection = () => (
    <div className="w-screen relative left-1/2 -translate-x-1/2 mt-12 pt-10 border-t border-slate-200">
      <div className="w-full max-w-7xl 2xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">

        {/* ── Main 2-column section ── */}
        <div className="grid lg:grid-cols-2">

          {/* Left – copy */}
          <div className="flex flex-col justify-center px-7 py-9 sm:px-10 sm:py-12 lg:py-14">
            {/* Eyebrow */}
            <div className="flex items-center gap-2 mb-5">
              <span className="h-px w-5 bg-orange-400" />
              <span className="text-[11px] font-bold tracking-[0.15em] uppercase text-orange-600">
                MüşteriX
              </span>
            </div>

            {/* Heading */}
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight mb-3">
              Arçelik &amp; Beko bayileri için<br className="hidden sm:block" /> kapsamlı yönetim sistemi
            </h3>

            {/* Body */}
            <p className="text-sm sm:text-[15px] text-slate-500 leading-relaxed mb-7 max-w-sm">
              Müşteri takibi, tahsilat süreçleri, bayi operasyonları ve canlı satış raporlamasını tek bir sade panelde birleştirin.
            </p>

            {/* Feature list */}
            <ul className="space-y-3 mb-8">
              {MUSTERIX_FEATURES.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-orange-600" />
                  </div>
                  <span className="text-sm text-slate-700">{label}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <a
              href="mailto:hello@idatech.online"
              className="inline-flex items-center gap-2 self-start rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              hello@idatech.online
            </a>
          </div>

          {/* Right – hero screenshot */}
          <div className="relative hidden lg:flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200/80 p-6 xl:p-10">
            <div className="w-full h-[400px] xl:h-[520px] rounded-xl overflow-hidden shadow-2xl ring-1 ring-black/10 flex items-center bg-slate-50">
              <img
                src="/musterix-screenshots/statistics.png"
                alt="MüşteriX Ürün Detayı"
                className="w-full h-full object-contain object-top"
              />
            </div>
          </div>
        </div>

        {/* ── Screenshot carousel ── */}
        {(() => {
          const slides = MUSTERIX_SCREENSHOTS;
          const prev = () => setCarouselIdx(i => (i - 1 + slides.length) % slides.length);
          const next = () => setCarouselIdx(i => (i + 1) % slides.length);
          const current = slides[carouselIdx];
          return (
            <div className="border-t border-slate-100 bg-slate-900">
              {/* Full-width image area with overlay nav */}
              <div className="relative w-full aspect-[16/10] min-h-[280px] sm:min-h-[360px] lg:min-h-[450px] xl:min-h-[520px]">
                <img
                  key={current.name}
                  src={`/musterix-screenshots/${current.name}.png`}
                  alt="MüşteriX ekran görüntüsü"
                  className="absolute inset-0 w-full h-full object-contain object-top bg-slate-900"
                />
                {/* Overlay nav buttons */}
                <button
                  onClick={prev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-colors z-10"
                  aria-label="Önceki"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-700" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-colors z-10"
                  aria-label="Sonraki"
                >
                  <ChevronRight className="w-5 h-5 text-slate-700" />
                </button>
                {/* Slide indicator overlay */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/40 text-white text-xs font-medium">
                  {carouselIdx + 1} / {slides.length}
                </div>
              </div>
              {/* Fixed caption bar */}
              <div className="px-6 sm:px-8 lg:px-10 py-4 bg-slate-900 border-t border-slate-700">
                <p className="text-sm font-medium text-slate-300">
                  MüşteriX — Arçelik &amp; Beko bayileri için mağaza yönetimi, tahsilat takibi ve satış raporlaması tek panelde.
                </p>
              </div>
              {/* Thumbnail strip */}
              <div className="px-6 sm:px-8 lg:px-10 py-4 border-t border-slate-800 flex gap-2 overflow-x-auto">
                {slides.map((item, idx) => (
                  <button
                    key={item.name}
                    onClick={() => setCarouselIdx(idx)}
                    className={`shrink-0 w-[100px] sm:w-[110px] rounded-lg overflow-hidden border-2 transition-all ${
                      idx === carouselIdx
                        ? 'border-orange-400 shadow-lg shadow-orange-400/20 ring-2 ring-orange-400/30'
                        : 'border-slate-700 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={`/musterix-screenshots/${item.name}.png`}
                      alt=""
                      className="w-full h-14 sm:h-16 object-cover object-top"
                    />
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <Banknote className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800">
              Ne Ödeyeceğim?
            </h1>
          </div>
          <p className="text-slate-500 text-sm sm:text-base max-w-xl mx-auto">
            Arçelik hesap akış dökümünü yükleyin, haftalık ödemenizi anında görün.
          </p>
        </div>

        {/* Bilgilendirme */}
        <Card className="mb-6 max-w-2xl mx-auto border-slate-200 bg-white/80 shadow-sm backdrop-blur-sm">
          <CardContent className="p-5 sm:p-6 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-slate-800">Önemli Bilgilendirme</p>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xl mx-auto">
                  Bu sayfadaki tüm hesaplamalar tarayıcınızda yapılır, MüşteriX bu sayfa için veri tutmaz.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xl mx-auto">
                  Hesap akışı canlı bir rapordur; değerler anlık değişebilir. Lütfen mutlaka kontrol ediniz.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xl mx-auto">
                  Bu ekran yalnızca kolay okunabilir bir özet sunar; nihai kontrol ve sorumluluk kullanıcıya aittir.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {!groups ? (
          <>
          <div className="max-w-2xl mx-auto">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`
                relative border-2 border-dashed rounded-2xl p-10 sm:p-16 text-center transition-all duration-300 cursor-pointer
                ${isDragging
                  ? 'border-blue-500 bg-blue-50 scale-[1.02] shadow-lg shadow-blue-100'
                  : 'border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-md'
                }
              `}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleInputChange}
              />
              <div className="flex flex-col items-center gap-4">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-colors ${isDragging ? 'bg-blue-100' : 'bg-slate-100'}`}>
                  {isDragging ? (
                    <FileSpreadsheet className="w-10 h-10 text-blue-500" />
                  ) : (
                    <Upload className="w-10 h-10 text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-700">
                    {isDragging ? 'Dosyayı bırakın...' : 'Hesap Akış Dökümünü Yükleyin'}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    Excel dosyasını sürükleyip bırakın veya tıklayarak seçin
                  </p>
                  <p className="text-xs text-slate-400 mt-3">.xlsx veya .xls formatı desteklenir</p>
                </div>
              </div>
            </div>
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

          </div>
          <div className="w-full max-w-[min(90rem,100%)] mx-auto mt-10 -mb-2">
            {renderMusterixSection()}
          </div>
          </>
        ) : computed && (
          <div className="space-y-6">
            {/* File Info */}
            <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-slate-700">{fileName}</span>
                <Badge variant="secondary" className="text-xs">{computed.totalTxCount} işlem</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={clearData} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4 mr-1" />
                Temizle
              </Button>
            </div>

            {/* Main Payment Card */}
            {computed.thisWeek && (
              <Card className={`
                border-0 shadow-xl overflow-hidden
                ${computed.thisWeek.payment > 0
                  ? 'bg-gradient-to-br from-orange-500 via-red-500 to-rose-600'
                  : 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600'
                }
              `}>
                <CardContent className="p-6 sm:p-10">
                  <div className="text-center text-white">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      {computed.thisWeek.payment > 0 ? (
                        <AlertTriangle className="w-6 h-6 text-white/80" />
                      ) : (
                        <CheckCircle2 className="w-6 h-6 text-white/80" />
                      )}
                      <p className="text-sm sm:text-base font-medium text-white/80 uppercase tracking-wider">
                        {computed.thisWeek.payment > 0 ? 'Bu Çarşamba Ödemeniz' : 'Bu Hafta'}
                      </p>
                    </div>
                    {computed.thisWeek.payment > 0 ? (
                      <>
                        <p className="text-4xl sm:text-6xl font-bold tracking-tight mt-2">
                          ₺{formatCurrency(computed.thisWeek.payment)}
                        </p>
                        <p className="text-sm text-white/70 mt-3">
                          {formatDateLong(computed.thisWeek.wednesdayDate)} tarihinde ödenecek
                        </p>
                        <div className="flex items-center justify-center gap-4 mt-4 text-sm text-white/60">
                          <span>Borç: ₺{formatCurrency(computed.thisWeek.periodBorc)}</span>
                          <span>·</span>
                          <span>Alacak: ₺{formatCurrency(computed.thisWeek.periodAlacak)}</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-3xl sm:text-5xl font-bold tracking-tight mt-2">
                        Bu hafta ödeme yok
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Wednesdays Carousel */}
            {computed.upcoming.length > 1 && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    Önümüzdeki Çarşamba Ödemeleri
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  <div className="overflow-x-auto">
                    <div className="flex gap-3 px-4 sm:px-6 pb-4 min-w-0">
                      {computed.upcoming.map((week, idx) => {
                        const hasPay = week.payment > 0;
                        const isFirst = idx === 0;
                        return (
                          <div
                            key={week.wednesdayDate}
                            className={`
                              shrink-0 rounded-xl p-4 min-w-[170px] border transition-all
                              ${isFirst
                                ? hasPay
                                  ? 'bg-red-50 border-red-200 ring-2 ring-red-300'
                                  : 'bg-green-50 border-green-200 ring-2 ring-green-300'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                              }
                            `}
                          >
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isFirst ? 'text-orange-600' : 'text-slate-400'}`}>
                              {isFirst ? 'Bu Hafta' : formatDateShort(week.wednesdayParsed)}
                            </p>
                            <p className="text-xs text-slate-500 mb-2">
                              {formatDateLong(week.wednesdayDate)}
                            </p>
                            {hasPay ? (
                              <p className="text-lg font-bold text-red-600 tabular-nums">
                                ₺{formatCurrency(week.payment)}
                              </p>
                            ) : (
                              <p className="text-sm font-bold text-green-600">Ödeme yok</p>
                            )}
                            <div className="text-xs text-slate-400 mt-1 tabular-nums">
                              <span>{week.totalTxCount} işlem</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats */}
            {computed.upcoming.length > 0 && (() => {
              const upcomingTotal = computed.upcoming.reduce((s, w) => s + w.payment, 0);
              const upcomingBorc = computed.upcoming.reduce((s, w) => s + w.periodBorc, 0);
              const upcomingAlacak = computed.upcoming.reduce((s, w) => s + w.periodAlacak, 0);
              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Banknote className="w-4 h-4 text-orange-500" />
                        <span className="text-xs text-slate-500">Toplam Ödeme</span>
                      </div>
                      <p className="text-lg font-bold text-red-600 tabular-nums">₺{formatCurrency(upcomingTotal)}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-slate-500">Toplam Borç</span>
                      </div>
                      <p className="text-lg font-bold text-slate-800 tabular-nums">₺{formatCurrency(upcomingBorc)}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingDown className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-slate-500">Toplam Alacak</span>
                      </div>
                      <p className="text-lg font-bold text-slate-800 tabular-nums">₺{formatCurrency(upcomingAlacak)}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarDays className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-slate-500">Ödeme Haftası</span>
                      </div>
                      <p className="text-lg font-bold text-slate-800">{computed.upcoming.length}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })()}

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'upcoming' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('upcoming')}
              >
                <CalendarDays className="w-4 h-4 mr-1.5" />
                Haftalık
              </Button>
              <Button
                variant={viewMode === 'monthly' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('monthly')}
              >
                <Calendar className="w-4 h-4 mr-1.5" />
                Aylık
              </Button>
            </div>

            {/* Weekly View */}
            {viewMode === 'upcoming' && (
              <div className="space-y-4">
                {computed.upcoming.length > 0 && (
                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <ArrowUpRight className="w-5 h-5 text-orange-500" />
                        Önümüzdeki Haftalar
                        <Badge variant="secondary" className="text-xs ml-1">{computed.upcoming.length} hafta</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-0 pb-0">
                      <div className="divide-y divide-slate-100">
                        {computed.upcoming.map((week, idx) =>
                          renderSettlementWeek(week, idx === 0 ? 'Bu Hafta' : undefined)
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {computed.past.length > 0 && (
                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <ArrowDownRight className="w-5 h-5 text-slate-400" />
                        Geçmiş Haftalar
                        <Badge variant="secondary" className="text-xs ml-1">{computed.past.length} hafta</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-0 pb-0">
                      <div className="divide-y divide-slate-100">
                        {[...computed.past].reverse().map(week => renderSettlementWeek(week))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Monthly View */}
            {viewMode === 'monthly' && (
              <div className="space-y-3">
                {computed.allMonths.map((month) => {
                  const isExpanded = expandedMonths.has(month.key);

                  return (
                    <Card key={month.key} className="border-slate-200 shadow-sm overflow-hidden">
                      <button
                        onClick={() => toggleMonth(month.key)}
                        className="w-full flex items-center justify-between px-4 sm:px-6 py-4 hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${month.totalPayment > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                            <Calendar className={`w-6 h-6 ${month.totalPayment > 0 ? 'text-red-500' : 'text-green-500'}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-base font-bold text-slate-800">{month.label}</p>
                            <p className="text-xs text-slate-400">
                              {month.weeks.length} hafta · {month.weeks.reduce((s, w) => s + w.totalTxCount, 0)} işlem
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <p className={`text-base font-bold tabular-nums ${month.totalPayment > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {month.totalPayment > 0
                                ? `₺${formatCurrency(month.totalPayment)}`
                                : 'Ödeme yok'
                              }
                            </p>
                            <div className="flex items-center gap-2 text-xs tabular-nums mt-0.5">
                              <span className="text-red-400">B: ₺{formatCurrency(month.totalBorc)}</span>
                              <span className="text-green-400">A: ₺{formatCurrency(month.totalAlacak)}</span>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-slate-200 divide-y divide-slate-100">
                          {month.weeks.map(week => renderSettlementWeek(week))}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Footer */}
            <div className="text-center pb-6">
              <p className="text-xs text-slate-400">
                Bu sayfa yalnızca Arçelik hesap akış dökümü Excel dosyalarını destekler.
                Verileriniz tarayıcınızda işlenir, herhangi bir sunucuya gönderilmez.
              </p>
            </div>

            {renderMusterixSection()}
          </div>
        )}
      </div>
    </div>
  );
};

export default NeOdeyecegim;
